import React, { Component } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Typo, Icon, ReviewStar } from 'tmt-ui-kit';
import xss from 'xss';

import { stripLink } from '~/utils/helpers';
import { getProductUrl } from '~/utils/trip';
import Link from '~/components/Link/Link';
// import GalleryReview from '~/components/General/Gallery/GalleryReview';
// import ReviewStar from './ReviewStar';
import ReviewTraveler from './ReviewTraveler';
import ReplyItem from './ReplyItem';
import ReplyInput from './ReplyInput';
import ReviewVote from './ReviewVote';

const GalleryReview = dynamic(
  () => import(/* webpackChunkName: "Gallery.GalleryReview.components" */'../General/Gallery/GalleryReview'),
  { loading: () => null }
);

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  color: ${props => props.theme.darkBlue} !important;

  ${props => props.isHideHelpful && `
    margin-top: 8px;
    margin-bottom: 16px;
  `}
`;

const ReviewColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TravelerColumn = styled.div`
  width: 265px;
  padding-left: 8px;

  @media(max-width: 800px) {
    width: 200px;
  }

  @media(max-width: 600px) {
    padding-left: 0px;
    width: auto;
  }
`;

const TripName = styled(Typo.HeaderFour)`
  margin-bottom: 8px !important;
  margin-top: 0px !important;
  color: ${props => props.theme.black} !important;
`;

const ReviewThreeWords = styled(Typo.Paragraph)`
  margin: 4px 0 0 0;
  font-size: 18px !important;
  font-weight: ${props => props.theme.fontWeight.medium} !important;
`;

const ReviewText = styled(Typo.Paragraph)`
  color: ${props => props.theme.darkBlue} !important;
  font-size: 16px !important;
  margin: 8px 0 0 0;
`;

const MobileContainer = styled.div`
  display: none;
  @media(max-width: 600px) {
    display: inherit;
  }
`;

const DesktopContainer = styled.div`
  @media(max-width: 600px) {
    display: none;
  }
`;

const MainContainer = styled.div`
  border-bottom: 2px solid ${props => props.theme.blueGray};
  padding-top: 16px;
`;

const Clickable = styled.button`
  background-color: transparent;
  color: inherit;
  border: none;
  padding: 0px;
  margin: 0px;
  cursor: pointer;
`;

const ReplyButton = styled(Clickable)`
  display: flex;
  flex-direction: row;
  align-items: center;
  opacity: 0.4;
  color: ${props => props.theme.black};
  font-size: 12px;
  padding-bottom: ${props => props.paddingBottom}px;
`;

const ReplyButtonLabel = styled(Typo.Paragraph)`
  margin: 0 0 0 8px;
  font-size: 12px !important;
`;

const BetweenRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 8px;
  margin-bottom: 16px;
`;

const ReplyContainer = styled.div`
  padding-left: 32px;
`;

class ReviewItem extends Component {
  state = {
    showReplyInput: false,
    replyText: '',
  };

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps.review._id !== this.props.review._id) {
      return true;
    }
    if (nextProps.review.vote !== this.props.review.vote) {
      return true;
    }
    if (this.props.user._id !== nextProps.user._id) {
      return true;
    }
    if (this.state.showReplyInput !== nextState.showReplyInput) {
      return true;
    }
    if (this.state.replyText !== nextState.replyText) {
      return true;
    }
    return false;
  }

  toggleReplyInput = () => {
    this.setState((state) => ({ showReplyInput: !state.showReplyInput }));
  }

  handleReply = text => {
    this.setState({ replyText: text });
    if (this.props.onReply) this.props.onReply(this.props.review._id, text);
  }

  handleVote = vote => {
    if (this.props.onVote) this.props.onVote(this.props.review._id, vote);
  }

  render() {
    const fullPath = this.props.url && this.props.url.query && this.props.url.query.fullPath;
    const {
      trip_id: trip,
      user_id: traveler,
      created_at: createdAt,
    } = this.props.review;
    const { isHideHelpful } = this.props;
    const { score, header, text, vote, images } = this.props.review;
    let { reply } = this.props.review;
    if (!reply && this.state.replyText.length > 0) {
      reply = { text: this.state.replyText };
    }
    const { name: tripName } = trip;
    const { local_expert_id: localExpert } = this.props.review;
    const { showReplyInput } = this.state;
    return (
      <MainContainer>
        <Container isHideHelpful={isHideHelpful}>
          <ReviewColumn>
            <MobileContainer>
              <TravelerColumn>
                <ReviewTraveler traveler={traveler} createdAt={createdAt} />
              </TravelerColumn>
            </MobileContainer>
            <Link to={getProductUrl(trip)}>
              <TripName>{tripName}</TripName>
            </Link>
            <ReviewStar score={score} />
            {header && <ReviewThreeWords>{header}</ReviewThreeWords>}
            {text && text.split('\n').map((line, idx) => (
              <ReviewText
                key={idx}
                dangerouslySetInnerHTML={{ __html: xss(fullPath ? stripLink(line, fullPath) : line) }}
              />
            ))}
            {images.length > 0 && <GalleryReview images={images} defaultAlt={tripName} />}
          </ReviewColumn>
          <DesktopContainer>
            <TravelerColumn>
              <ReviewTraveler traveler={traveler} createdAt={createdAt} />
            </TravelerColumn>
          </DesktopContainer>
        </Container>
        <BetweenRow>
          {!isHideHelpful && <ReviewVote vote={vote} handleVote={this.handleVote} />}
          {isHideHelpful && <div />}
          <TravelerColumn>
            {(!(showReplyInput || reply)
              && this.props.user._id
              && localExpert._id
              && (this.props.user._id.toString() === localExpert._id.toString()))
              && (
                <ReplyButton onClick={this.toggleReplyInput}>
                  <Icon name="reply" />
                  <ReplyButtonLabel>Reply</ReplyButtonLabel>
                </ReplyButton>
              )}
          </TravelerColumn>
        </BetweenRow>
        <ReplyContainer>
          {reply && <ReplyItem reply={reply} user={localExpert} />}
          {showReplyInput && !reply && <ReplyInput handleReply={this.handleReply} cancelReply={this.toggleReplyInput} />}
        </ReplyContainer>
      </MainContainer>
    );
  }
}

ReviewItem.propTypes = {
  review: PropTypes.object,
  user: PropTypes.object,
  url: PropTypes.object,
  isHideHelpful: PropTypes.bool,
  onVote: PropTypes.func,
  onReply: PropTypes.func,
};

export default ReviewItem;
